'use client'

import { DUR } from '@/animations/settings/vars'
import { cn } from '@/lib/utils'
import { Base, Container, Img, Section, SectionHeader } from '@/ui/components'
import { MEDIA } from '@spot'
import PhotoSwipeLightbox from 'photoswipe/lightbox'
import { memo, useEffect } from 'react'
import { SectionGalleryProps } from './type'

const SectionGallery = ({
  sectionHeaderData,
  images,
  section
}: SectionGalleryProps) => {
  const limit = 3
  const galleryId = `gallery-${section?.id?.toString()}`

  useEffect(() => {
    // Lightbox CSS is only needed once the lightbox opens (on click), so load
    // it lazily instead of shipping it in the render-blocking CSS.
    void import('photoswipe/style.css')

    let lightbox = new PhotoSwipeLightbox({
      gallery: `#${galleryId}`,
      children: 'a',
      pswpModule: () => import('photoswipe'),
      loop: false,
      showHideAnimationType: 'fade'
    })

    lightbox.on('uiRegister', function () {
      lightbox?.pswp?.ui?.registerElement({
        name: 'custom-caption',
        order: 9,
        isButton: false,
        appendTo: 'root',
        html: 'Caption text',
        onInit: (el) => {
          lightbox?.pswp?.on('change', () => {
            const currSlideElement = lightbox?.pswp?.currSlide?.data?.element
            let captionHTML = ''
            if (currSlideElement) {
              const hiddenCaption = currSlideElement.querySelector(
                '.hidden-caption-content'
              )
              if (hiddenCaption) {
                // get caption from element with class hidden-caption-content
                captionHTML = hiddenCaption.innerHTML
              } else {
                // get caption from alt attribute
                captionHTML =
                  currSlideElement?.querySelector('img')?.getAttribute('alt') ||
                  ''
              }
            }
            el.innerHTML = captionHTML || ''
          })
        }
      })
    })

    lightbox.init()

    return () => {
      lightbox.destroy()
      lightbox = null as unknown as PhotoSwipeLightbox
    }
  }, [galleryId])

  if (!images || images?.length === 0) {
    return null
  }

  const hasMore = images.length > limit

  // Ff the images are less than 3, then show 3 images adding the last image 3 times
  const imagesToShow =
    images.length < 3
      ? [...images, ...Array(3 - images.length).fill(images[images.length - 1])]
      : images

  return (
    <Section data={section} className="s-section-gallery py-none">
      <SectionHeader {...sectionHeaderData} />
      <Container anim="fade-in" className="py-md">
        <div
          className={'pswp-gallery gap-xs grid-rows-auto grid md:grid-cols-3'}
          id={galleryId}
        >
          {imagesToShow?.map((image, index) => (
            <Base
              key={galleryId + '-' + index.toString()}
              anim="fade-in"
              className={cn(
                'group relative overflow-hidden',
                index === 0 && 'col-span-2 md:row-span-2',
                index >= limit && 'hidden',
                index === 0
                  ? 'aspect-spacer col-span-2 md:row-span-2'
                  : 'max-md:aspect-rect'
              )}
              animVars={{ delay: DUR.stagger * (index + 1) }}
            >
              {index < images.length ? (
                <a
                  href={image.publicUrl}
                  data-pswp-width={image.properties?.dimensions?.width}
                  data-pswp-height={image.properties?.dimensions?.height}
                  rel="noreferrer"
                  target="_blank"
                >
                  {hasMore && index === limit - 1 && (
                    <div className="textstyle-md-bold pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-black/70 text-white">
                      {`${images.length - limit} Bilder anzeigen`}
                    </div>
                  )}
                  <Img
                    key={index?.toString()}
                    data={image}
                    fill
                    crop={{
                      width: index === 0 ? 1100 : 550,
                      height: index === 0 ? 740 : 370
                    }}
                    sizes={`${MEDIA.tablet} ${index === 0 ? '100vw' : '50vw'}, 57.5rem`}
                    objectCover
                    className={cn(
                      'h-full w-full',
                      index >= images.length && 'blur-lg'
                    )}
                    hoverGroupEffect={index < images.length}
                  />
                  <div className="hidden-caption-content hidden">
                    {image.properties?.description ||
                      image.properties?.alternative ||
                      ''}
                  </div>
                </a>
              ) : (
                <span>
                  <Img
                    key={index?.toString()}
                    data={image}
                    fill
                    crop={{
                      width: index === 0 ? 1100 : 550,
                      height: index === 0 ? 740 : 370
                    }}
                    sizes={`${MEDIA.tablet} ${index === 0 ? '100vw' : '50vw'}, 57.5rem`}
                    objectCover
                    className={cn(
                      'h-full w-full',
                      index >= images.length && 'blur-lg'
                    )}
                  />
                </span>
              )}
            </Base>
          ))}
        </div>
      </Container>
    </Section>
  )
}

SectionGallery.displayName = 'SectionGallery'

export default memo(SectionGallery)
